<template>
	<div class="message-list" ref="messageList">
		<div v-if="messages.length === 0" class="empty-state">
			<div class="empty-icon">💬</div>
			<h3>开始新对话</h3>
			<p>我是您的建筑钢结构专业AI助手，有什么可以帮助您的？</p>

			<div class="suggestions">
				<div v-for="suggestion in suggestions" :key="suggestion" class="suggestion-chip"
					@click="$emit('send', suggestion)">
					{{ suggestion }}
				</div>
			</div>
		</div>

		<div v-else class="messages">
			<div v-for="message in messages" :key="message.id" class="message" :class="message.role">
				<div class="message-avatar" v-if="message.role === 'user'">
					{{ message.role === 'user' ? '👤' : '🤖' }}
				</div>
				<div class="message-avatar" v-if="message.role != 'user'">
					<img src="../../assets/image/login02.png" alt="AI助手" />
				</div>
				<div class="message-content">
					<div class="message-text" v-html="formatMessage(message.content)"></div>
					<div class="message-time">{{ formatTime(message.timestamp) }}</div>
				</div>
			</div>

			<div v-if="loading" class="message assistant">
				<div class="message-avatar"> <img src="../../assets/image/login02.png" alt="AI助手" /></div>
				<div class="message-content">
					<div class="typing-indicator">
						<div class="typing-dot"></div>
						<div class="typing-dot"></div>
						<div class="typing-dot"></div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'MessageList',

		props: {
			messages: {
				type: Array,
				default: () => []
			},
			loading: {
				type: Boolean,
				default: false
			}
		},

		data() {
			return {
				suggestions: [
					'如何计算钢结构的荷载？',
					'钢结构焊接工艺要点',
					'生成钢结构设计PPT课件',
					'中建钢构现场工程师要求'
				]
			}
		},

		watch: {
			messages() {
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			},
			loading() {
				this.$nextTick(() => {
					this.scrollToBottom()
				})
			}
		},

		methods: {
			formatMessage(content) {
				// 将markdown格式转换为HTML
				return content
					.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
					.replace(/\n/g, '<br>')
					.replace(/- (.*?)(?=\n|$)/g, '<li>$1</li>')
					.replace(/(\d+)\. (.*?)(?=\n|$)/g, '<li>$2</li>')
			},

			formatTime(timestamp) {
				const date = new Date(timestamp)
				return date.toLocaleTimeString('zh-CN', {
					hour: '2-digit',
					minute: '2-digit'
				})
			},

			scrollToBottom() {
				const container = this.$refs.messageList
				container.scrollTop = container.scrollHeight
			}
		}
	}
</script>

<style scoped>
	.message-list {
		flex: 1;
		overflow-y: auto;
		padding: 16px 20px;
		background: #f8f9fa;
	}

	.empty-state {
		text-align: center;
		padding: 60px 20px;
	}

	.empty-icon {
		font-size: 72px;
		margin-bottom: 24px;
	}

	.empty-state h3 {
		font-size: 24px;
		font-weight: 600;
		color: var(--text-dark);
		margin-bottom: 12px;
	}

	.empty-state p {
		font-size: 16px;
		color: var(--text-light);
		margin-bottom: 32px;
		max-width: 600px;
		margin-left: auto;
		margin-right: auto;
	}

	.suggestions {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
		gap: 12px;
		max-width: 800px;
		margin: 0 auto;
	}

	.suggestion-chip {
		background: white;
		border: 1px solid var(--border-light);
		border-radius: 20px;
		padding: 12px 20px;
		font-size: 14px;
		color: var(--text-dark);
		cursor: pointer;
		transition: all 0.3s;
		text-align: center;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.suggestion-chip:hover {
		background: var(--primary);
		color: white;
		border-color: var(--primary);
		transform: translateY(-2px);
	}

	.messages {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.message {
		display: flex;
		gap: 16px;
		max-width: 80%;
		animation: slideUp 0.3s ease-out;
	}

	.message.user {
		align-self: flex-end;
		flex-direction: row-reverse;
	}

	.message-avatar {
		width: 40px;
		height: 40px;
		background: var(--bg-glass);
		backdrop-filter: blur(var(--blur-md));
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 20px;
		flex-shrink: 0;
	}

	.message-avatar img {
		width: 100%;
		border-radius: 50%;
	}

	.message.assistant .message-avatar {
		background: linear-gradient(135deg, var(--primary), var(--secondary));
	}

	.message-content {
		background: white;
		padding: 12px 16px;
		border-radius: 16px;
		box-shadow: var(--shadow-sm);
		min-width: 0; /* 允许内容收缩 */
		flex: 1;
	}

	.message.user .message-content {
		background: var(--primary);
		color: white;
	}

	.message-text {
		font-size: 16px;
		line-height: 1.6;
		word-wrap: break-word;
		overflow-wrap: break-word;
	}

	.message-text strong {
		font-weight: 600;
	}

	.message-text li {
		list-style: none;
		padding-left: 20px;
		position: relative;
		margin: 8px 0;
	}

	.message-text li:before {
		content: '•';
		position: absolute;
		left: 0;
	}

	.message-time {
		font-size: 12px;
		opacity: 0.6;
		margin-top: 8px;
	}

	.typing-indicator {
		display: flex;
		gap: 6px;
		padding: 8px 0;
	}

	.typing-dot {
		width: 10px;
		height: 10px;
		background: var(--text-light);
		border-radius: 50%;
		animation: typing 1.4s infinite;
	}

	.typing-dot:nth-child(2) {
		animation-delay: 0.2s;
	}

	.typing-dot:nth-child(3) {
		animation-delay: 0.4s;
	}

	@keyframes typing {
		0%, 60%, 100% {
			opacity: 0.3;
			transform: translateY(0);
		}
		30% {
			opacity: 1;
			transform: translateY(-10px);
		}
	}

	@keyframes slideUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	/* 平板设备 (768px - 1024px) */
	@media (max-width: 1024px) {
		.message-list {
			padding: 20px;
		}

		.message {
			max-width: 85%;
		}

		.suggestions {
			grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		}
	}

	/* 手机设备 (小于768px) */
	@media (max-width: 768px) {
		.message-list {
			padding: 12px 16px;
		}

		.empty-state {
			padding: 30px 16px;
		}

		.empty-icon {
			font-size: 48px;
			margin-bottom: 16px;
		}

		.empty-state h3 {
			font-size: 20px;
			margin-bottom: 8px;
		}

		.empty-state p {
			font-size: 14px;
			margin-bottom: 24px;
		}

		.suggestions {
			grid-template-columns: 1fr;
			gap: 8px;
		}

		.suggestion-chip {
			padding: 10px 16px;
			font-size: 13px;
			white-space: normal;
		}

		.messages {
			gap: 10px;
		}

		.message {
			max-width: 95%;
			gap: 10px;
		}

		.message-avatar {
			width: 32px;
			height: 32px;
			font-size: 16px;
		}

		.message-content {
			padding: 10px 14px;
			border-radius: 14px;
		}

		.message-text {
			font-size: 14px;
			line-height: 1.5;
		}

		.message-text li {
			padding-left: 16px;
			margin: 6px 0;
		}

		.message-time {
			font-size: 11px;
			margin-top: 6px;
		}
	}

	/* 小屏手机 (小于480px) */
	@media (max-width: 480px) {
		.message-list {
			padding: 12px 8px;
		}

		.empty-state {
			padding: 30px 12px;
		}

		.empty-icon {
			font-size: 40px;
		}

		.empty-state h3 {
			font-size: 18px;
		}

		.empty-state p {
			font-size: 13px;
		}

		.suggestion-chip {
			padding: 8px 12px;
			font-size: 12px;
		}

		.message {
			max-width: 98%;
			gap: 8px;
		}

		.message-avatar {
			width: 28px;
			height: 28px;
			font-size: 14px;
		}

		.message-content {
			padding: 10px 12px;
			border-radius: 12px;
		}

		.message-text {
			font-size: 13px;
		}

		.typing-indicator {
			padding: 6px 0;
		}

		.typing-dot {
			width: 8px;
			height: 8px;
		}
	}

	/* 超宽屏设备 (大于1200px) */
	@media (min-width: 1200px) {
		.message-list {
			padding: 32px;
		}

		.message {
			max-width: 70%;
		}

		.suggestions {
			grid-template-columns: repeat(2, 1fr);
			gap: 16px;
			max-width: 600px;
		}
	}

	/* 横屏手机适配 */
	@media (max-height: 500px) and (orientation: landscape) {
		.empty-state {
			padding: 20px 16px;
		}

		.empty-icon {
			font-size: 36px;
			margin-bottom: 12px;
		}

		.empty-state h3 {
			font-size: 16px;
			margin-bottom: 6px;
		}

		.empty-state p {
			font-size: 12px;
			margin-bottom: 16px;
		}

		.suggestions {
			grid-template-columns: repeat(2, 1fr);
		}
	}
</style>